import React from "react";
import { useTheme } from "@carrier-io/air-react/styles";
import Box from "@carrier-io/air-react/Box";
import { CreateUserHead } from "./CreateUserHead";
import Button from "@carrier-io/air-react/Button";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import Stack from "@carrier-io/air-react/Stack";
import { Control, FieldErrors, useFieldArray } from "react-hook-form";
import { UserInput } from "../../validations/userCreateValidation";
import { ControllableInput } from "../controllable/Input";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@carrier-io/air-react/IconButton";
import { Skeleton } from "@carrier-io/air-react";

type CreateUserAdditionalAttributesProps = {
  control: Control<UserInput>;
  errors?: Pick<FieldErrors<UserInput>, "attributes">;
  isLoading?: boolean;
};

export const CreateUserAdditionalAttributes = ({
  control,
  errors,
  isLoading,
}: CreateUserAdditionalAttributesProps) => {
  const theme = useTheme();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "attributes",
  });

  const handleAddAttribute = () => {
    append({ key: "", value: "" });
  };

  return (
    <Box
      data-testid="additional-attributes-section"
      sx={{
        paddingBottom: 3,
        marginBottom: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CreateUserHead
        title="Additional Attributes"
        description="Specify additional attributes for this user."
      />
      {isLoading ? (
        <Skeleton
          data-testid="attributes-loading-skeleton"
          height={127}
          width={300}
        />
      ) : (
        fields.map((item, index) => (
          <Stack
            key={item.id}
            direction="row"
            spacing={1}
            alignItems="start"
            sx={{ marginBottom: 2 }}
            data-testid={`attribute-row-${index}`}
          >
            <ControllableInput
              label="Name field"
              error={errors?.attributes?.[index]?.key?.message}
              hideBackgroundColor
              showBorder
              required
              size="xlarge"
              control={control}
              name={`attributes.${index}.key`}
              data-testid={`attribute-key-${index}`}
            />
            <ControllableInput
              label="Value field"
              error={errors?.attributes?.[index]?.value?.message}
              hideBackgroundColor
              showBorder
              required
              size="xlarge"
              control={control}
              name={`attributes.${index}.value`}
              data-testid={`attribute-value-${index}`}
            />
            <IconButton
              sx={{ marginTop: "8px !important" }}
              size="medium"
              data-testid={`remove-attribute-${index}`}
              onClick={() => remove(index)}
            >
              <DeleteOutlineIcon
                sx={{
                  height: "100%",
                  width: "100%",
                }}
              />
            </IconButton>
          </Stack>
        ))
      )}

      <Button
        variant="text"
        startIcon={<AddOutlinedIcon fontSize="small" />}
        onClick={handleAddAttribute}
        data-testid="add-attribute-button"
        sx={{
          height: "inherit !important",
          padding: 0,
          ":hover": { backgroundColor: "transparent" },
        }}
      >
        Add an Additional Attribute
      </Button>
    </Box>
  );
};
